// src/services/UserService.js

import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp 
} from "firebase/firestore";
import { updateProfile, updateEmail, updatePassword } from "firebase/auth";
import { auth, db } from "../firebase";

const USERS_COLLECTION = "users";

// Get current user profile
export const getUserProfile = async (userId = null) => {
  try {
    // Use the authenticated user ID if not provided
    const uid = userId || auth.currentUser?.uid;
    
    if (!uid) {
      throw new Error("User not authenticated");
    }
    
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    return { id: userDoc.id, ...userDoc.data() };
    
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (userData, updateAuthProfile = true) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    
    const userRef = doc(db, USERS_COLLECTION, user.uid);
    
    // Update Firestore document
    await updateDoc(userRef, {
      ...userData,
      updatedAt: Timestamp.now()
    });
    
    // Update auth profile if needed
    if (updateAuthProfile && userData.name) {
      await updateProfile(user, {
        displayName: userData.name
      });
    }
    
    return { id: user.uid, ...userData };
    
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

// Update user email
export const updateUserEmail = async (newEmail, password) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    
    // First reauthenticate user (if implementation needed)
    // For simplicity, we're assuming the user just logged in
    
    // Update Firebase Auth email
    await updateEmail(user, newEmail);
    
    // Update Firestore user document
    const userRef = doc(db, USERS_COLLECTION, user.uid);
    await updateDoc(userRef, {
      email: newEmail,
      updatedAt: Timestamp.now()
    });
    
    return { success: true, email: newEmail };
    
  } catch (error) {
    console.error("Error updating user email:", error);
    throw error;
  }
};

// Update user password
export const updateUserPassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    
    // First reauthenticate user (if implementation needed)
    // For simplicity, we're assuming the user just logged in
    
    // Update Firebase Auth password
    await updatePassword(user, newPassword);
    
    return { success: true };
    
  } catch (error) {
    console.error("Error updating user password:", error);
    throw error;
  }
};

// Admin: Get all users
export const getAllUsers = async (role = null) => {
  try {
    let q;
    
    if (role) {
      q = query(
        collection(db, USERS_COLLECTION),
        where("role", "==", role),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(
        collection(db, USERS_COLLECTION),
        orderBy("createdAt", "desc")
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting users:", error);
    throw error;
  }
};

// Admin: Update user role
export const updateUserRole = async (userId, role) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    await updateDoc(userRef, {
      role,
      updatedAt: Timestamp.now()
    });
    
    return { id: userId, role };
    
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

// Admin: Get user statistics
export const getUserStatistics = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, USERS_COLLECTION));
    
    let totalUsers = 0;
    let customers = 0;
    let admins = 0;
    let pharmacists = 0;
    
    usersSnapshot.forEach(doc => {
      const user = doc.data();
      totalUsers++;
      
      if (user.role === "customer") {
        customers++;
      } else if (user.role === "admin") {
        admins++;
      } else if (user.role === "pharmacist") {
        pharmacists++;
      }
    });
    
    return {
      totalUsers,
      customers,
      admins,
      pharmacists
    };
    
  } catch (error) {
    console.error("Error getting user statistics:", error);
    throw error;
  }
};

export default {
  getUserProfile,
  updateUserProfile,
  updateUserEmail,
  updateUserPassword,
  getAllUsers,
  updateUserRole,
  getUserStatistics
};